import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Toaster } from '../components/ui/toaster';
import { Shield, Database, Lock, Users, Cookie, Globe, FileText, Mail } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <Toaster />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-orange-50 via-white to-red-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-100 to-red-100 text-orange-700 px-5 py-2.5 rounded-full text-sm font-bold mb-8">
            <Shield className="w-4 h-4" />
            Privacy Policy
          </div>
          <h1 className="text-5xl lg:text-7xl font-black mb-8">
            Privacy&nbsp;
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">
              Policy
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Last Updated: January 2025
          </p>
        </div>
      </section>

      {/* Policy Content */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          
          {/* Introduction */}
          <div className="mb-12 p-8 bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl border-2 border-orange-100">
            <p className="text-lg text-gray-700 leading-relaxed">
              At AALIGN, we value your privacy and are committed to protecting your personal and business data. This Privacy Policy explains how we collect, use, store, and protect information when you use our platform.
            </p>
          </div>

          {/* 1. Information We Collect */}
          <div className="mb-12">
            <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
              <Database className="w-8 h-8 text-blue-600" />
              1. Information We Collect
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              We collect the following types of information:
            </p>
            <div className="space-y-6">
              <div className="p-6 bg-blue-50 rounded-xl">
                <h3 className="text-xl font-bold text-gray-900 mb-3">1.1. Personal Information:</h3>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">Name, email address, contact number, company details.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">Login credentials and role assignments.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">Billing information (if applicable).</span>
                  </li>
                </ul>
              </div>
              
              <div className="p-6 bg-purple-50 rounded-xl">
                <h3 className="text-xl font-bold text-gray-900 mb-3">1.2. Usage Data:</h3>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">IP address, browser type, operating system.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">Pages visited, features used, time spent on the platform.</span>
                  </li>
                </ul>
              </div>
              
              <div className="p-6 bg-green-50 rounded-xl">
                <h3 className="text-xl font-bold text-gray-900 mb-3">1.3. Client Data:</h3>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-600 rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">Campaigns, leads, email lists, and any custom data you upload.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-600 rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">CRM usage patterns, metadata from campaign execution and lead tracking.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* 2. How We Use Your Information */}
          <div className="mb-12 p-8 bg-white rounded-2xl border-2 border-gray-200">
            <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
              <FileText className="w-8 h-8 text-indigo-600" />
              2. How We Use Your Information
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              We use your information to:
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <span className="font-bold text-gray-900 flex-shrink-0">2.1</span>
                <p className="text-lg text-gray-700">Provide and manage your access to AALIGN CRM.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-bold text-gray-900 flex-shrink-0">2.2</span>
                <p className="text-lg text-gray-700">Process payments and manage subscriptions.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-bold text-gray-900 flex-shrink-0">2.3</span>
                <p className="text-lg text-gray-700">Monitor and improve platform performance and user experience.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-bold text-gray-900 flex-shrink-0">2.4</span>
                <p className="text-lg text-gray-700">Respond to inquiries, support requests, and troubleshoot issues.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-bold text-gray-900 flex-shrink-0">2.5</span>
                <p className="text-lg text-gray-700">Send service-related communications (e.g., feature updates, billing alerts).</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-bold text-gray-900 flex-shrink-0">2.6</span>
                <p className="text-lg text-gray-700">Comply with legal obligations or enforce our terms.</p>
              </div>
            </div>
          </div>

          {/* 3. Data Sharing and Disclosure */}
          <div className="mb-12">
            <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
              <Users className="w-8 h-8 text-cyan-600" />
              3. Data Sharing and Disclosure
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <span className="font-bold text-gray-900 flex-shrink-0">3.1</span>
                <p className="text-lg text-gray-700">We do not sell, rent, or trade your personal or business data.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-bold text-gray-900 flex-shrink-0">3.2</span>
                <p className="text-lg text-gray-700">We may share data with third-party service providers (e.g., payment processors, hosting services) bound by confidentiality agreements.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-bold text-gray-900 flex-shrink-0">3.3</span>
                <p className="text-lg text-gray-700">As required by law, court order, or regulatory authority. In connection with a merger, acquisition, or sale of assets, with prior notice.</p>
              </div>
            </div>
          </div>

          {/* 4. Data Security */}
          <div className="mb-12 p-8 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border-2 border-green-100">
            <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
              <Lock className="w-8 h-8 text-green-600" />
              4. Data Security
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <span className="font-bold text-gray-900 flex-shrink-0">4.1</span>
                <div>
                  <p className="text-lg text-gray-700 mb-3">We implement technical and organizational measures to protect your data, including:</p>
                  <ul className="space-y-2 ml-6">
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-green-600 rounded-full mt-3 flex-shrink-0"></div>
                      <span className="text-lg text-gray-700">SSL encryption</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-green-600 rounded-full mt-3 flex-shrink-0"></div>
                      <span className="text-lg text-gray-700">Role-based access control</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-green-600 rounded-full mt-3 flex-shrink-0"></div>
                      <span className="text-lg text-gray-700">Regular vulnerability scanning and backups</span>
                    </li>
                  </ul>
                  <p className="text-lg text-gray-700 mt-3">While we take reasonable precautions, no platform can guarantee absolute security. You are responsible for safeguarding your login credentials.</p>
                </div>
              </div>
            </div>
          </div>

          {/* 5. Data Retention */}
          <div className="mb-12">
            <h2 className="text-3xl font-black mb-6">5. Data Retention</h2>
            <div className="flex items-start gap-3">
              <span className="font-bold text-gray-900 flex-shrink-0">5.1</span>
              <p className="text-lg text-gray-700">We retain user and client data for as long as your account is active or as needed to provide our services. Upon account termination, data may be retained for up to 30 days unless otherwise requested or required by law.</p>
            </div>
          </div>

          {/* 6. Your Rights */}
          <div className="mb-12 p-8 bg-white rounded-2xl border-2 border-gray-200">
            <h2 className="text-3xl font-black mb-6">6. Your Rights</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <span className="font-bold text-gray-900 flex-shrink-0">6.1</span>
                <div>
                  <p className="text-lg text-gray-700 mb-3">Depending on your jurisdiction, you may have rights to:</p>
                  <ul className="space-y-2 ml-6">
                    <li className="flex items-start gap-3">
                      <span className="font-bold text-gray-900">6.1.1</span>
                      <span className="text-lg text-gray-700">Access or correct your personal data.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="font-bold text-gray-900">6.1.2</span>
                      <span className="text-lg text-gray-700">Request deletion of your account and associated data.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="font-bold text-gray-900">6.1.3</span>
                      <span className="text-lg text-gray-700">Withdraw consent (where applicable).</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="font-bold text-gray-900">6.1.4</span>
                      <span className="text-lg text-gray-700">Object to certain data uses or request restrictions.</span>
                    </li>
                  </ul>
                  <p className="text-lg text-gray-700 mt-4">
                    To exercise your rights, contact us at{' '}
                    <a href="mailto:support@aalign.io" className="text-orange-600 hover:text-orange-700 font-semibold underline">
                      support@aalign.io
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 7. Cookies and Tracking Technologies */}
          <div className="mb-12">
            <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
              <Cookie className="w-8 h-8 text-amber-600" />
              7. Cookies and Tracking Technologies
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <span className="font-bold text-gray-900 flex-shrink-0">7.1</span>
                <div>
                  <p className="text-lg text-gray-700 mb-3">We use cookies and similar technologies to:</p>
                  <ul className="space-y-2 ml-6">
                    <li className="flex items-start gap-3">
                      <span className="font-bold text-gray-900">7.1.1</span>
                      <span className="text-lg text-gray-700">Remember user preferences</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="font-bold text-gray-900">7.1.2</span>
                      <span className="text-lg text-gray-700">Analyze platform usage</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="font-bold text-gray-900">7.1.3</span>
                      <span className="text-lg text-gray-700">Enhance functionality</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-bold text-gray-900 flex-shrink-0">7.2</span>
                <p className="text-lg text-gray-700">You can control cookies through your browser settings. Disabling them may impact functionality.</p>
              </div>
            </div>
          </div>

          {/* 8. International Data Transfers */}
          <div className="mb-12 p-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border-2 border-blue-100">
            <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
              <Globe className="w-8 h-8 text-blue-600" />
              8. International Data Transfers
            </h2>
            <div className="flex items-start gap-3">
              <span className="font-bold text-gray-900 flex-shrink-0">8.1</span>
              <p className="text-lg text-gray-700">If you access AALIGN CRM from outside our hosting country, your data may be transferred and processed in that location. By using our platform, you consent to this transfer.</p>
            </div>
          </div>

          {/* 9. Changes to This Policy */}
          <div className="mb-12">
            <h2 className="text-3xl font-black mb-6">9. Changes to This Policy</h2>
            <div className="flex items-start gap-3">
              <span className="font-bold text-gray-900 flex-shrink-0">9.1</span>
              <p className="text-lg text-gray-700">We may update this Privacy Policy periodically. Changes will be communicated via email or dashboard notification. Continued use of the platform implies acceptance of the updated policy.</p>
            </div>
          </div>

          {/* 10. Contact Us */}
          <div className="mb-12 p-8 bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl text-white">
            <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
              <Mail className="w-8 h-8" />
              10. Contact Us
            </h2>
            <p className="text-lg leading-relaxed mb-6">
              For questions or concerns regarding this policy or your personal data, please contact:
            </p>
            <div className="space-y-3">
              <p className="text-lg">
                <strong>Email:</strong>{' '}
                <a href="mailto:support@aalign.io" className="text-orange-100 hover:text-white font-semibold underline">
                  support@aalign.io
                </a>
              </p>
              <p className="text-lg">
                <strong>Website:</strong>{' '}
                <a href="https://www.aalign.io" className="text-orange-100 hover:text-white font-semibold underline">
                  www.aalign.io
                </a>
              </p>
            </div>
          </div>

          {/* Contact CTA */}
          <div className="mt-16 p-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl text-center border-2 border-gray-200">
            <h3 className="text-2xl font-bold mb-4 text-gray-900">Questions About Your Privacy?</h3>
            <p className="text-lg mb-6 text-gray-600">
              We're here to help you understand how we protect your data.
            </p>
            <a
              href="mailto:support@aalign.io"
              className="inline-block bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold px-8 py-3 rounded-xl shadow-lg transition-all transform hover:scale-105"
            >
              Contact Support
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
